/**
 * useColorModeValue Hook
 * Internal hook for design system components to get color mode reactive values
 */

import { useColorModeValue as useChakraColorModeValue } from "@chakra-ui/react";
import {
  TextColorToken,
  SurfaceColorToken,
  BorderColorToken,
  InteractiveColorToken,
  PrimitiveColorToken,
  InvertedColorToken,
  resolveColorWithMode,
} from "design-system/foundation/Color";

type ColorToken = TextColorToken | SurfaceColorToken | BorderColorToken | InteractiveColorToken | PrimitiveColorToken | InvertedColorToken;

/**
 * Hook that returns a color value that automatically updates when color mode changes
 * This is used internally by design system components
 */
export const useColorModeValue = (token: ColorToken): string => {
  // Handle inverted color tokens specially
  if (token && typeof token === "object" && "_semantic" in token && token._semantic === "inverted") {
    const invertedToken = token as InvertedColorToken;
    // For inverted colors, swap the light and dark mode values
    return useChakraColorModeValue(
      resolveColorWithMode(invertedToken._originalToken, "dark"), // Use dark value in light mode
      resolveColorWithMode(invertedToken._originalToken, "light"), // Use light value in dark mode
    );
  }

  // Handle regular color tokens
  return useChakraColorModeValue(
    resolveColorWithMode(token, "light"),
    resolveColorWithMode(token, "dark"),
  );
};

/**
 * Hook for conditional values based on color mode
 * Similar to Chakra's useColorModeValue but for design system tokens
 */
export const useColorModeToken = <T>(lightValue: T, darkValue: T): T => useChakraColorModeValue(lightValue, darkValue);

/**
 * Hook that returns resolved color values for both light and dark modes
 * More efficient when you need multiple colors from the same token
 */
export const useColorModeColors = (tokens: ColorToken[]) => {
  const lightColors = tokens.map((token) => {
    // Handle inverted tokens
    if (token && typeof token === "object" && "_semantic" in token && token._semantic === "inverted") {
      const invertedToken = token as InvertedColorToken;
      return resolveColorWithMode(invertedToken._originalToken, "dark"); // Use dark value in light mode
    }
    return resolveColorWithMode(token, "light");
  });

  const darkColors = tokens.map((token) => {
    // Handle inverted tokens
    if (token && typeof token === "object" && "_semantic" in token && token._semantic === "inverted") {
      const invertedToken = token as InvertedColorToken;
      return resolveColorWithMode(invertedToken._originalToken, "light"); // Use light value in dark mode
    }
    return resolveColorWithMode(token, "dark");
  });

  return tokens.map((_, index) => useChakraColorModeValue(lightColors[index], darkColors[index]));
};
